import type { SimulatorAppDefinition, SimulatorProfileDefinition } from "../../contracts/appManifest.js";

export interface Win11DonorAppDescriptor {
  action: string;
  fdAppId: string;
  icon: string;
  name: string;
  payload?: string;
  type: "app";
}

const donorEquivalents: Record<string, Win11DonorAppDescriptor> = {
  calc: {
    action: "CALCUAPP",
    fdAppId: "calc",
    icon: "calculator",
    name: "Calculator",
    type: "app"
  },
  cmd: {
    action: "TERMINAL",
    fdAppId: "cmd",
    icon: "terminal",
    name: "Terminal",
    type: "app"
  },
  explorer: {
    action: "EXPLORER",
    fdAppId: "explorer",
    icon: "explorer",
    name: "File Explorer",
    type: "app"
  },
  notepad: {
    action: "NOTEPAD",
    fdAppId: "notepad",
    icon: "notepad",
    name: "Notepad",
    type: "app"
  },
  paint: {
    action: "WHITEBOARD",
    fdAppId: "paint",
    icon: "board",
    name: "Whiteboard",
    type: "app"
  },
  "server-manager": {
    action: "FD_SERVERMANAGER",
    fdAppId: "server-manager",
    icon: "fd-servermanager",
    name: "Server Manager-style Console",
    type: "app"
  },
  settingsSystem: {
    action: "SETTINGS",
    fdAppId: "settingsSystem",
    icon: "settings",
    name: "Settings",
    type: "app"
  },
  taskmgr: {
    action: "TASKMANAGER",
    fdAppId: "taskmgr",
    icon: "taskmanager",
    name: "Task Manager",
    type: "app"
  }
};

const staticFirstDeskDonorApps: Record<string, Win11DonorAppDescriptor> = {
  lusrmgr: {
    action: "FD_LUSRMGR",
    fdAppId: "lusrmgr",
    icon: "fd-lusrmgr",
    name: "Local Users and Groups",
    type: "app"
  },
  ncpa: {
    action: "FD_NCPA",
    fdAppId: "ncpa",
    icon: "fd-ncpa",
    name: "Network Connections",
    type: "app"
  },
  sysdm: {
    action: "FD_SYSDM",
    fdAppId: "sysdm",
    icon: "fd-sysdm",
    name: "System Properties",
    type: "app"
  }
};

const directApps = {
  ...donorEquivalents,
  ...staticFirstDeskDonorApps
};

export function isWin11NativeDonorApp(appId: string): boolean {
  return appId in donorEquivalents;
}

export function firstDeskAppIdForDonorAction(action: string | undefined, icon: string | undefined): string | null {
  const matched = Object.values(directApps).find((app) => app.action === action || app.icon === icon);
  return matched?.fdAppId ?? null;
}

export function donorActionForApp(appId: string): string {
  return directApps[appId]?.action ?? `FDOPEN_${safeId(appId).toUpperCase().replaceAll("-", "_")}`;
}

export function donorIconForApp(appId: string): string {
  return directApps[appId]?.icon ?? `fd-${safeId(appId)}`;
}

export function donorNameForApp(app: SimulatorAppDefinition): string {
  return directApps[app.id]?.name ?? app.shortTitle ?? app.title;
}

export function toWin11DonorApp(app: SimulatorAppDefinition): Win11DonorAppDescriptor {
  return directApps[app.id] ?? {
    action: donorActionForApp(app.id),
    fdAppId: app.id,
    icon: donorIconForApp(app.id),
    name: donorNameForApp(app),
    type: "app"
  };
}

export function win11ProfileDesktopApps(
  apps: SimulatorAppDefinition[],
  profile: SimulatorProfileDefinition
): Win11DonorAppDescriptor[] {
  const desktopIds = new Set(profile.desktopAppIds);
  return apps.filter((app) => desktopIds.has(app.id) || app.desktop).map(toWin11DonorApp);
}

export function win11ProfilePinnedApps(
  apps: SimulatorAppDefinition[],
  profile: SimulatorProfileDefinition
): Win11DonorAppDescriptor[] {
  const pinnedIds = new Set(profile.pinnedAppIds);
  return apps.filter((app) => pinnedIds.has(app.id) || app.pinned).map(toWin11DonorApp);
}

function safeId(value: string): string {
  return value
    .trim()
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    || "app";
}
